import React, { useCallback, useState } from 'react';

export interface FieldConfig {
  label: string;
  min: number;
  max: number;
  unitMsg: string;
}

export type FieldConfigs = Record<string, FieldConfig>;

export type ValidationRule =
  | { type: 'required'; message?: string }
  | { type: 'requiredGreaterThanZero'; message?: string }
  | { type: 'compareWith'; logic: (data: any) => boolean; message?: string }
  | { type: 'numeric'; min?: number; max?: number; unitMsg?: string; message?: string }
  | { type: 'select'; message?: string }
  | { type: 'date'; message?: string }
  | { type: 'custom'; logic: (data: any) => boolean; message?: string };

export interface FieldValidationConfig {
  label: string;
  rules: ValidationRule[];
}

// Helper to check install number uniqueness
function isInstallNumberDuplicate(installNumber: any, completions: any[], currentId: any, isEditMode?: boolean) {
  return completions.some((c: any) =>
    isEditMode
      ? c.InstallNumber === installNumber && (c.InstallationId ?? c.installationId) !== currentId
      : c.InstallNumber === installNumber,
  );
}

// Helper to generate conditional TVD rule for pump sections
const pumpTvdRule = (
  section: 'Upper' | 'Mid' | 'Lower',
): { type: 'custom'; logic: (data: any) => boolean; message: string } => ({
  type: 'custom',
  logic: (data: any) => {
    const typeKey = `pump${section}Type`;
    const tvdKey = `pump${section}TVD`;
    if (data[typeKey]) {
      const val = data[tvdKey];
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return !!val && !isNaN(num) && num > 0;
    }
    return true;
  },
  message: 'Value must be greater than 0',
});

// custom date order validation rules
function toDateOnly(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const dateOrderRule = (
  field: string,
  compareTo: string,
  message: string,
): { type: 'custom'; logic: (data: any) => boolean; message: string } => ({
  type: 'custom',
  logic: (data: any) => {
    if (!data[field] || !data[compareTo]) return true;
    const a = new Date(data[field]);
    const b = new Date(data[compareTo]);
    if (isNaN(a.getTime()) || isNaN(b.getTime())) return true;
    const aDate = toDateOnly(a);
    const bDate = toDateOnly(b);
    return aDate.getTime() >= bDate.getTime();
  },
  message,
});

const cumulativeDateOrderRules = (
  field: string,
  previousFields: string[],
  label: string,
  previousLabels: string[],
): { type: 'custom'; logic: (data: any) => boolean; message: string }[] =>
  previousFields.map((prev, i) =>
    dateOrderRule(field, prev, `${label} must be greater than or equal to ${previousLabels[i]}`),
  );

export const TAB_VALIDATION_CONFIGS: Record<string, Record<string, FieldValidationConfig>> = {
  installation: {
    liftType: {
      label: 'Lift type',
      rules: [{ type: 'required' }],
    },
    installNumber: {
      label: 'Install number',
      rules: [{ type: 'required' }, { type: 'numeric', min: 1, max: 999999 }],
    },
  },
  runlife: {
    installDate: {
      label: 'Install date',
      rules: [{ type: 'required' }],
    },
    startDate: {
      label: 'Start date',
      rules: cumulativeDateOrderRules('startDate', ['installDate'], 'Start date', ['Install date']),
    },
    failDate: {
      label: 'Fail date',
      rules: cumulativeDateOrderRules('failDate', ['installDate', 'startDate'], 'Fail date', [
        'Install date',
        'Start date',
      ]),
    },
    pullDate: {
      label: 'Pull Date',
      rules: cumulativeDateOrderRules('pullDate', ['installDate', 'startDate', 'failDate'], 'Pull date', [
        'Install date',
        'Start date',
        'Fail date',
      ]),
    },
    teardownDate: {
      label: 'Teardown date',
      rules: cumulativeDateOrderRules(
        'teardownDate',
        ['installDate', 'startDate', 'failDate', 'pullDate'],
        'Teardown date',
        ['Install date', 'Start date', 'Fail date', 'Pull date'],
      ),
    },
  },
  glCompletion: {
    reservoirPressure: {
      label: 'Reservoir pressure',
      rules: [{ type: 'requiredGreaterThanZero' }],
    },
    gasGravityInjected: {
      label: 'Gas gravity - injected',
      rules: [{ type: 'requiredGreaterThanZero' }],
    },
    oilGravity: {
      label: 'Oil gravity',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric', min: 6, max: 55, unitMsg: 'API' }],
    },
    solutionGasOilRatio: {
      label: 'Solution gas oil ratio',
      rules: [{ type: 'select' }],
    },
    formationVolumeFactorCorrelation: {
      label: 'Formation volume factor correlation',
      rules: [{ type: 'select' }],
    },
    viscosityCorrelation: {
      label: 'Viscosity correlation',
      rules: [{ type: 'select' }],
    },
    inflowModel: {
      label: 'Inflow model',
      rules: [{ type: 'select' }],
    },
    ohtc: {
      label: 'OHTC',
      rules: [{ type: 'numeric', min: 0.1, max: 20 }],
    },
  },
  fluid: {
    oilGravity: {
      label: 'Oil gravity',
      rules: [{ type: 'required' }, { type: 'numeric', min: 6, max: 55, unitMsg: 'API' }],
    },
    gasGravity: {
      label: 'Gas gravity',
      rules: [{ type: 'required' }, { type: 'numeric', min: 0.6, max: 1.6, unitMsg: 'S.G.' }],
    },
    reservoirPressure: {
      label: 'Reservoir pressure',
      rules: [{ type: 'required' }, { type: 'numeric', min: 300, max: 20000, unitMsg: 'psig' }],
    },
    reservoirTemperature: {
      label: 'Reservoir temperature',
      rules: [{ type: 'required' }, { type: 'numeric', min: 300, max: 500, unitMsg: 'F' }],
    },
    inflowModel: {
      label: 'Inflow model',
      rules: [{ type: 'required' }],
    },
  },
  pumps: {
    pumpUpperManufacturer: {
      label: 'Upper section manufacturer',
      rules: [{ type: 'required' }],
    },
    pumpUpperSeries: {
      label: 'Upper section series',
      rules: [{ type: 'required' }],
    },
    pumpUpperType: {
      label: 'Upper section type',
      rules: [{ type: 'required' }],
    },
    pumpUpperTVD: {
      label: 'Upper section pump 1 # stages',
      rules: [{ type: 'required' }, pumpTvdRule('Upper'), { type: 'numeric' }],
    },
    pumpMidTVD: {
      label: 'Mid section pump 2 # stages',
      rules: [pumpTvdRule('Mid'), { type: 'numeric' }],
    },
    pumpLowerTVD: {
      label: 'Lower section pump 3 # stages',
      rules: [pumpTvdRule('Lower'), { type: 'numeric' }],
    },
  },
  completion: {
    OHTCDefault: {
      label: 'OHTC (BTU/hrft2F)',
      rules: [{ type: 'numeric', min: 0.1, max: 20 }],
    },
    PumpMeasured: {
      label: 'Pump measured',
      rules: [
        { type: 'requiredGreaterThanZero' },
        { type: 'numeric' },
        {
          type: 'compareWith',
          logic: (data: any) => Number(data.PumpTrueVertical) > Number(data.PumpMeasured),
          message: 'Pump MD must be greater than pump TVD',
        },
      ],
    },
    PumpTrueVertical: {
      label: 'Pump TVD (ft)',
      rules: [
        { type: 'requiredGreaterThanZero' },
        { type: 'numeric' },
        {
          type: 'compareWith',
          logic: (data: any) => {
            return Number(data.PumpMeasured) < Number(data.PumpTrueVertical);
          },
          message: 'Pump TVD must be less than or equal to pump MD',
        },
      ],
    },
    BottomholeMeasured: {
      label: 'Top of Perfs MD (ft)',
      rules: [
        { type: 'requiredGreaterThanZero' },
        { type: 'numeric' },
        {
          type: 'compareWith',
          logic: (data: any) => {
            const difference = (Number(data.PumpMeasured) || 0) - (Number(data.PumpTrueVertical) || 0);
            return (Number(data.BottomholeMeasured) || 0) < (Number(data.BottomholeTrueVertical) || 0) + difference;
          },
          message: 'Measured depth delta must be greater than or equal to true vertical depth delta',
        },
      ],
    },
    BottomholeTrueVertical: {
      label: 'Top of Perfs TVD (ft)',
      rules: [
        { type: 'requiredGreaterThanZero' },
        { type: 'numeric' },
        {
          type: 'compareWith',
          logic: (data: any) => {
            const difference = (Number(data.PumpMeasured) || 0) - (Number(data.PumpTrueVertical) || 0);
            return (Number(data.BottomholeMeasured) || 0) < (Number(data.BottomholeTrueVertical) || 0) + difference;
          },
          message: 'Measured depth delta must be greater than or equal to true vertical depth delta',
        },
      ],
    },
    GeometryTubingOD: {
      label: 'Tubing OD (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    GeometryTubingID: {
      label: 'Tubing ID (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    GeometryTubingRoughness: {
      label: 'Tubing Rgh (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    GeometryCasingOD: {
      label: 'Casing OD (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    GeometryCasingID: {
      label: 'Casing ID (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    GeometryCasingRoughness: {
      label: 'Casing Rgh (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    MotorOD: {
      label: 'Motor OD (in)',
      rules: [{ type: 'numeric' }],
    },
    MotorHp60Hz: {
      label: 'Motor power @60 Hz (HP)',
      rules: [{ type: 'numeric' }],
    },
    MotorVolts60Hz: {
      label: 'Motor voltage @60 Hz (Volts)',
      rules: [{ type: 'numeric' }],
    },
    MotorCurrents60Hz: {
      label: 'Motor current @60 Hz (Amps)',
      rules: [{ type: 'numeric' }],
    },
    VsdNameplateAmps: {
      label: 'VSD nameplate current (Amps)',
      rules: [{ type: 'numeric' }],
    },
    VsdNameplateKva: {
      label: 'VSD nameplate power (KVA)',
      rules: [{ type: 'numeric' }],
    },
    MotorToPumpSpeedRatio: {
      label: 'Motor to pump speed ratio',
      rules: [{ type: 'required' }, { type: 'numeric', min: 1, max: 4 }],
    },
    GeometryPumpIntakeRestriction: {
      label: 'Pump intake I/D (in)',
      rules: [{ type: 'requiredGreaterThanZero' }, { type: 'numeric' }],
    },
    ShaftLimitHp60Hz: {
      label: 'Shaft limit (HP)',
      rules: [{ type: 'numeric' }],
    },
  },
};

export const validateFieldWithRules = (value: any, config: FieldValidationConfig, data?: any): string => {
  for (const rule of config.rules) {
    let error = '';

    switch (rule.type) {
      case 'required':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          error = rule.message || `${config.label} is required`;
        }
        break;

      case 'requiredGreaterThanZero':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          error = rule.message || `${config.label} is required`;
        } else {
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          if (isNaN(numValue) || numValue <= 0) {
            error = rule.message || `${config.label} must be greater than 0`;
          }
        }
        break;

      case 'numeric':
        if (value && value.toString().trim() !== '') {
          const stringValue = value.toString();
          if (!/^\d*\.?\d+$/.test(stringValue)) {
            error = rule.message || `${config.label} must be a number`;
          } else {
            const numValue = parseFloat(stringValue);
            if (rule.min !== undefined && rule.max !== undefined && (numValue < rule.min || numValue > rule.max)) {
              error =
                rule.message ||
                `${config.label} must be between ${rule.min} and ${rule.max} ${rule.unitMsg || ''}`.trim();
            }
          }
        }
        break;
      case 'compareWith':
        if (data && rule.logic(data) && rule.message) {
          error = rule.message || '';
        }
        break;

      case 'select':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          error = rule.message || `${config.label} is required`;
        }
        break;

      case 'date':
        if (!value) {
          error = rule.message || `${config.label} is required`;
        }
        break;

      case 'custom':
        if (data && rule.logic && !rule.logic(data)) {
          error = rule.message || '';
        }
        break;
    }

    if (error) return error;
  }

  return '';
};

export const validateTabFields = (
  tabName: string,
  data: Record<string, any>,
  options?: { completions?: any[]; isEditMode?: boolean },
): Record<string, string> => {
  const tabConfig = TAB_VALIDATION_CONFIGS[tabName];
  if (!tabConfig) return {};

  const errors: Record<string, string> = {};

  Object.entries(tabConfig).forEach(([fieldName, fieldConfig]) => {
    const error = validateFieldWithRules(data[fieldName], fieldConfig, data);
    if (error) errors[fieldName] = error;
  });

  // Install number uniqueness
  if (tabName === 'installation' && options?.completions && data.installNumber) {
    const currentId = data.InstallationId ?? data.installationId;
    if (isInstallNumberDuplicate(data.installNumber, options.completions, currentId, options.isEditMode)) {
      errors.installNumber = 'Install Number already exists';
    }
  }

  return errors;
};

export function getMopFromIndustryApplicationId(industryApplicationId: number): string {
  switch (industryApplicationId) {
    case 7:
      return 'gl';
    case 4:
      return 'esp';
    case 6:
      return 'gapl';
    default:
      return '';
  }
}

export function getMOPforCompletionWarnings(industryApplicationId: number): string {
  switch (industryApplicationId) {
    case 7:
      return 'GAS';
    case 4:
      return 'ESP';
    case 6:
      return 'GAPL';
    default:
      return '';
  }
}

export const completionsValidateField = (name: string, value: string, fieldConfigs: FieldConfigs): string => {
  const config = fieldConfigs[name];
  if (!config) return '';
  if (value.trim() === '') return `${config.label} is required`;
  if (!/^\d*\.?\d+$/.test(value)) return `${config.label} must be a number`;
  const num = parseFloat(value);
  if (num < config.min || num > config.max)
    return `Value must be between ${config.min} and ${config.max} ${config.unitMsg}`;
  return '';
};

export const completionsValidateRequiredField = (
  name: string,
  value: string,
  requiredFields: Record<string, string>,
): string => {
  const label = requiredFields[name];
  if (!label) return '';
  if (!value || value.trim() === '') return `${label} is required`;
  return '';
};

export const completionsValidateRequiredGreaterThanZero = (
  name: string,
  value: string,
  requiredFields: Record<string, string>,
): string => {
  const label = requiredFields[name];
  if (!label) return '';

  if (!value || value.trim() === '') return `${label} is required`;

  if (!/^\d*\.?\d+$/.test(value.trim())) return `${label} must be a number`;

  const num = parseFloat(value);
  if (isNaN(num) || num <= 0) return 'Value must be greater than 0';

  return '';
};

export const parseApiDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

export const validateInstallationTab = (installation: any, completions?: any[], isEditMode?: boolean): string[] => {
  const errorsObj = validateTabFields('installation', installation, { completions, isEditMode });
  return Object.values(errorsObj).filter(Boolean);
};

export const validatePumpsTab = (pumps: any): string[] =>
  Object.values(validateTabFields('pumps', pumps)).filter(Boolean);

export const validateCompletionTab = (completion: any): string[] =>
  Object.values(validateTabFields('completion', completion)).filter(Boolean);

export const validateRunlifeTab = (runlife: any): string[] =>
  Object.values(validateTabFields('runlife', runlife)).filter(Boolean);

export const validateFluidTab = (fluid: any): string[] =>
  Object.values(validateTabFields('fluid', fluid)).filter(Boolean);

export const validateGLCompletionTab = (glCompletion: any): string[] =>
  Object.values(validateTabFields('glCompletion', glCompletion)).filter(Boolean);

export const validateAllTabs = (
  data: any,
  selectedLiftType: string,
  completions?: any[],
  isEditMode?: boolean,
): { [tabName: string]: string[] } => {
  const tabErrors: { [tabName: string]: string[] } = {};

  tabErrors.installation = validateInstallationTab(data.installation, completions, isEditMode);
  tabErrors.runlife = validateRunlifeTab(data.runlife);

  if (selectedLiftType === '3') {
    tabErrors.completion = validateGLCompletionTab(data.glCompletion);
  } else if (selectedLiftType === '2') {
    tabErrors.fluid = validateFluidTab(data.fluid);
    tabErrors.pumps = validatePumpsTab(data.pumpData);
    tabErrors.completion = validateCompletionTab(data.completionData);
  }

  return tabErrors;
};

export interface UseFormValidationOptions {
  tabName: string;
  initialData?: Record<string, any>;
}

export interface UseFormValidationReturn {
  errors: Record<string, string>;
  validateField: (fieldName: string, value: any) => string;
  validateAllFields: (data: Record<string, any>) => Record<string, string>;
  clearErrors: () => void;
  clearFieldError: (fieldName: string) => void;
  setErrors: (errors: Record<string, string>) => void;
  hasErrors: boolean;
}

export const useFormValidation = (options: UseFormValidationOptions, data?: any): UseFormValidationReturn => {
  const { tabName } = options;
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = useCallback(
    (fieldName: string, value: any): string => {
      const tabConfig = TAB_VALIDATION_CONFIGS[tabName];
      if (!tabConfig?.[fieldName]) return '';

      const error = validateFieldWithRules(value, tabConfig[fieldName], data);
      setErrors((prev) => ({ ...prev, [fieldName]: error }));
      return error;
    },
    [tabName, data],
  );

  const validateAllFields = useCallback(
    (data: Record<string, any>): Record<string, string> => {
      const fieldErrors = validateTabFields(tabName, data);
      setErrors(fieldErrors);
      return fieldErrors;
    },
    [tabName],
  );

  const clearErrors = useCallback(() => setErrors({}), []);

  const clearFieldError = useCallback((fieldName: string) => {
    setErrors((prev) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [fieldName]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const hasErrors = Object.values(errors).some(Boolean);

  return {
    errors,
    validateField,
    validateAllFields,
    clearErrors,
    clearFieldError,
    setErrors,
    hasErrors,
  };
};

export const useFormFieldHandler = <T extends Record<string, any>>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  validation: UseFormValidationReturn,
  setStateCallback?: (fieldName: T) => void,
) => {
  return useCallback(
    (fieldName: keyof T, value: any) => {
      setState((prev) => {
        const updatedData = { ...prev, [fieldName]: value };
        setStateCallback?.(updatedData);
        return updatedData;
      });
      validation.validateField(fieldName as string, value);
    },
    [setState, validation],
  );
};

export function mapApiErrorsToFieldValidation(
  errorResponse: Record<string, string[]>,
  tabName: string,
  setFieldValidationErrors: React.Dispatch<
    React.SetStateAction<{ [tabName: string]: { [fieldName: string]: string } }>
  >,
) {
  setFieldValidationErrors((prev) => ({
    ...prev,
    [tabName]: {
      ...prev[tabName],
      ...Object.fromEntries(Object.entries(errorResponse).map(([field, messages]) => [field, messages[0]])),
    },
  }));
}

// Generic search utility for table data
export interface SearchConfig {
  searchableColumns: Array<{ key: string; selected: boolean }>;
  getEquipmentDisplayValue?: (row: any, columnId: string) => string;
  dateColumns?: Set<string>;
  statusColumns?: Set<string>;
  equipmentColumns?: Set<string>;
  htmlColumns?: Set<string>;
  checkboxColumns?: Set<string>;
}

export const createTableSearchFilter = (config: SearchConfig) => {
  const {
    searchableColumns,
    getEquipmentDisplayValue,
    dateColumns = new Set(['InstallDate', 'StartDate', 'FailDate', 'PullDate']),
    statusColumns = new Set(['Status', 'Well Status']),
    equipmentColumns = new Set(['EspEquipment', 'GLEquipment', 'GAPLEquipment']),
    htmlColumns = new Set(['ReportDownloadButtonsHtml', 'DocumentDownloadButtonsHtml']),
    checkboxColumns = new Set(['BlockUpdateOverride']),
  } = config;

  return (data: any[], searchTerm: string): any[] => {
    if (!searchTerm?.trim()) {
      return data;
    }

    const searchTermLower = searchTerm.toLowerCase().trim();
    const selectedColumns = searchableColumns.filter((col) => col.selected);

    return data.filter((row) => {
      // Always check Status column first (priority column)
      const statusValue = row.Status || '';
      if (statusValue && statusValue.toLowerCase().includes(searchTermLower)) {
        return true;
      }

      for (const col of selectedColumns) {
        let value = '';

        if (dateColumns.has(col.key) || statusColumns.has(col.key)) {
          value = row[col.key] || '';
        } else if (equipmentColumns.has(col.key) && getEquipmentDisplayValue) {
          value = getEquipmentDisplayValue(row, col.key) || '';
        } else if (htmlColumns.has(col.key)) {
          const htmlValue = row[col.key];
          if (htmlValue && typeof htmlValue === 'string') {
            value = htmlValue.replace(/<[^>]*>/g, '').trim();
          }
        } else if (checkboxColumns.has(col.key)) {
          const cellValue = row[col.key];
          value =
            cellValue === true || (typeof cellValue === 'string' && cellValue.toLowerCase() === 'true')
              ? 'true'
              : 'false';
        } else {
          const cellValue = row[col.key];
          if (cellValue != null) {
            value = typeof cellValue === 'object' ? JSON.stringify(cellValue) : String(cellValue);
          }
        }

        if (value && value.toLowerCase().includes(searchTermLower)) {
          return true;
        }
      }

      return false;
    });
  };
};
